import React, { useState, useEffect, useRef, useCallback } from 'react';
import { navigate } from 'gatsby';
import Button from '@ingka/button';
import SSRIcon from '@ingka/ssr-icon';
import upIcon from '@ingka/ssr-icon/paths/chevron-up';
import styled from 'styled-components';
import useTranslation from '../hooks/useTranslation';
import variables from '@ingka/variables/design-tokens';
import cssIngka from '@ingka/variables/colours-css';
import { useCart } from '../contexts/Cart';
import { ItemImage } from './Item';
import { useToggleBoolean } from '../hooks/useToggleBoolean';
import { CartItemDisplay } from './CartItemDisplay';
import { AspectRatioImage } from './AspectRatioImage';
import TMarkUp from './TMarkUp';

const StyledButton = styled(Button)`
  font-size: 1rem;
`;
const HorizontalItems = styled.div`
  display: flex;
  align-items: center;
  ${ItemImage}:nth-child(1) {
    margin-inline-start: 0px;
  }
`;
const MobileCartHeader = styled.button.attrs({ type: 'button' })`
  min-height: 80px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: start;
`;
const StyledOpenButtonContainer = styled.div`
  background: ${cssIngka.colourNeutral1};
  z-index: 2;
  padding: 0 24px;
  min-height: 95px;
  padding-top: 10px;
`;
const RotateIcon = styled.span`
  position: absolute;
  right: 20px;
  display: flex;
  align-items: center;
  height: var(--height);
  &[data-rotate='true'] {
    svg {
      transform: rotate(180deg);
    }
  }
`;
const MobileCartLayout = styled.div`
  --height: 65px;
  position: relative;
  bottom: 0;
  left: 0;
  z-index: 200;
  transform: translateY(100%);
  width: 100%;
  transition-duration: 400ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.4, 1);
  display: flex;
  flex-direction: column;
  &[data-open='true'] {
    transform: translateY(0);
  }
`;
const MobileCartItems = styled.div`
  width: 100%;
  height: var(--height);
  background: ${cssIngka.colourNeutral1};
  border-top: 1px solid ${cssIngka.colourNeutral3};
  transition-duration: 400ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.4, 1);
  &[data-open='true'] {
    height: calc(100vh - 200px);
  }
`;

const VerticalItems = styled.ul`
  padding: 0 24px;
  margin: 0;
  height: calc(100% - 80px);
  overflow-y: scroll;
  list-style: none;

  --distance: 65px;
  --width: 8px;

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar {
    width: var(--distance);
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: var(--distance);
    border: calc((var(--distance) / 2) - (var(--width) / 2)) solid
      rgba(0, 0, 0, 0);
    background-clip: padding-box;
    background-color: ${cssIngka.colourNeutral3};
  }
`;
const SRLiveRegion = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;
const CircleButton = styled.span`
  display: block;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${cssIngka.colourNeutral3};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${variables.fontWeightBold};
  color: ${cssIngka.colourNeutral7};
`;

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const MobileCart = (): JSX.Element => {
  const [open, toggleOpen, setOpen] = useToggleBoolean(false);
  const [liveMessage, setLiveMessage] = useState('');
  const Cart = useCart();
  const { t } = useTranslation();

  const cartRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const previousOpenRef = useRef(false);

  const count = Cart.state.items.size;
  const totalQuantity = Cart.getTotalNrOfItems();
  const inView = count > 0;
  const extraItems = Math.max(count - 3, 0);

  // Focus management: move focus into drawer on open, return to trigger on close
  useEffect(() => {
    if (open && !previousOpenRef.current) {
      requestAnimationFrame(() => {
        const firstFocusable =
          cartRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        firstFocusable?.focus();
      });
    } else if (!open && previousOpenRef.current) {
      triggerRef.current?.focus();
    }
    previousOpenRef.current = open;
  }, [open]);

  // Esc key dismissal
  useEffect(() => {
    if (!open) return;
    const handleEscKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [open, setOpen]);

  // Focus trap: keep tab focus within the drawer while open
  const handleFocusTrap = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key !== 'Tab' || !open || !cartRef.current) return;
      const focusableElements =
        cartRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements.length === 0) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    },
    [open]
  );

  useEffect(() => {
    if (totalQuantity > 0) {
      setLiveMessage(
        t('spareParts.a11yText.cartItemCount', { count: totalQuantity })
      );
    } else {
      setLiveMessage('');
    }
  }, [totalQuantity, t]);

  const horizontalItems = Array.from(Cart.state.items.values())
    .slice(0, 3)
    .map((item) => (
      <ItemImage key={item.item.id}>
        <AspectRatioImage
          src={item.item.imageSrc}
          alt={`${item.item.name}: ${item.item.description}`}
        />
      </ItemImage>
    ));

  if (extraItems > 0) {
    horizontalItems.push(
      <CircleButton
        key="extra"
        aria-hidden="true"
      >{`+${extraItems}`}</CircleButton>
    );
  }

  const verticalItems = Array.from(Cart.state.items.values()).map(
    ({ item, quantity }) => (
      <CartItemDisplay key={item.id} item={item} quantity={quantity} />
    )
  );

  return (
    <MobileCartLayout
      ref={cartRef}
      data-open={inView}
      aria-hidden={!inView}
      role={open ? 'dialog' : 'region'}
      aria-modal={open || undefined}
      aria-label={t('spareParts.orderList')}
      onKeyDown={handleFocusTrap}
    >
      <SRLiveRegion aria-live="polite" aria-atomic="true">
        {liveMessage}
      </SRLiveRegion>
      <MobileCartItems data-open={open}>
        {Cart.state.items.size > 0 && (
          <MobileCartHeader
            ref={triggerRef}
            onClick={toggleOpen}
            aria-expanded={open}
            aria-label={t('spareParts.orderList')}
            aria-controls={open ? 'mobile-cart-items' : undefined}
          >
            {open ? (
              <h2 className="h3">
                <TMarkUp value={t(`spareParts.orderList`)} />
              </h2>
            ) : (
              <HorizontalItems>{horizontalItems}</HorizontalItems>
            )}
            <RotateIcon data-rotate={open}>
              <SSRIcon paths={upIcon} aria-hidden="true" />
            </RotateIcon>
          </MobileCartHeader>
        )}
        {open && (
          <VerticalItems id="mobile-cart-items" role="list">
            {verticalItems}
          </VerticalItems>
        )}
      </MobileCartItems>

      {Cart.state.items.size > 0 && (
        <StyledOpenButtonContainer>
          <StyledButton
            type="emphasised"
            fluid
            onClick={() => navigate('/customer-information/')}
          >
            <TMarkUp value={t('spareParts.orderSpareParts')} />
          </StyledButton>
        </StyledOpenButtonContainer>
      )}
    </MobileCartLayout>
  );
};

export default MobileCart;
